import { useState, useEffect, useCallback } from 'react';
import { Bell, CheckCircle, Info, AlertCircle, XCircle, Check, Trash2, RefreshCw, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { toast } from 'sonner';

const PAGE_SIZE = 20;

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchNotifications = useCallback(async (reset = true) => {
    if (reset) {
      setLoading(true);
      setNotifications([]);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const skip = reset ? 0 : notifications.length;
      const response = await api.get(`/notifications?skip=${skip}&limit=${PAGE_SIZE}`);
      const newData = response.data || [];
      
      if (reset) {
        setNotifications(newData);
      } else {
        setNotifications(prev => [...prev, ...newData]);
      }
      
      setHasMore(newData.length === PAGE_SIZE);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      toast.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [notifications.length]);

  useEffect(() => {
    fetchNotifications(true);
  }, []);

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      fetchNotifications(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('All marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const clearAll = async () => {
    if (!window.confirm('Delete all notifications?')) return;
    try {
      await api.delete('/notifications/clear-all');
      setNotifications([]);
      setHasMore(false);
      toast.success('All cleared');
    } catch (error) {
      toast.error('Failed to clear');
    }
  };

  const getIcon = (type) => {
    const icons = {
      success: <CheckCircle className="h-5 w-5 text-emerald-600" />,
      warning: <AlertCircle className="h-5 w-5 text-amber-600" />,
      error: <XCircle className="h-5 w-5 text-rose-600" />
    };
    return icons[type] || <Info className="h-5 w-5 text-blue-600" />;
  };

  const formatTime = (dateString) => {
    const diff = Math.floor((Date.now() - new Date(dateString)) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  // Simple filtering (client-side on loaded data)
  const filtered = filter === 'all' 
    ? notifications 
    : filter === 'unread' 
      ? notifications.filter(n => !n.read)
      : notifications.filter(n => n.read);

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  return (
    <div data-testid="notifications-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-slate-900">Notifications</h1>
          {unreadCount > 0 && (
            <Badge className="bg-rose-500 text-white">{unreadCount} unread</Badge>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => fetchNotifications(true)}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={markAllAsRead}>
              <Check className="h-4 w-4 mr-1" /> Mark all read
            </Button>
          )}
          {notifications.length > 0 && (
            <Button variant="outline" size="sm" onClick={clearAll} className="text-rose-600">
              <Trash2 className="h-4 w-4 mr-1" /> Clear all
            </Button>
          )}
        </div>
      </div>

      {/* Simple Filter Tabs */}
      <div className="flex gap-2">
        {['all', 'unread', 'read'].map(f => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
            className={filter === f ? 'bg-[#4A238F]' : ''}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>

      {/* Notifications List */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Bell className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No notifications</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(n => (
            <Card 
              key={n.id} 
              className={`${n.read ? 'opacity-60' : 'border-l-4 border-l-[#4A238F]'}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-slate-100">
                    {getIcon(n.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{n.title}</span>
                      <Badge variant="outline" className="text-xs">{n.type}</Badge>
                      {!n.read && <span className="w-2 h-2 bg-blue-500 rounded-full" />}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{n.message}</p>
                    <p className="text-xs text-slate-400 mt-1">{formatTime(n.created_at)}</p>
                  </div>
                  <div className="flex gap-1">
                    {!n.read && (
                      <Button size="sm" variant="ghost" onClick={() => markAsRead(n.id)} className="h-8 w-8 p-0">
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => deleteNotification(n.id)} className="h-8 w-8 p-0 text-slate-400 hover:text-rose-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {/* Load More Button */}
          {hasMore && filter === 'all' && (
            <div className="text-center pt-4">
              <Button 
                variant="outline" 
                onClick={loadMore} 
                disabled={loadingMore}
                className="w-full max-w-xs"
              >
                {loadingMore ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4A238F]"></div>
                    Loading...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <ChevronDown className="h-4 w-4" />
                    Load More
                  </span>
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
